import React, { useEffect, useState } from "react";
import { db } from "../firebase";

import {
  collection,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";

import { useNavigate } from "react-router-dom";

import {
  FaArrowLeft,
  FaFutbol,
  FaUsers,
  FaMoneyBillWave,
  FaClock,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
  FaSyncAlt,
  FaUser,
  FaTrophy,
} from "react-icons/fa";

import "./UserGain.css";


/* =========================================================
   SETTINGS
========================================================= */

const CACHE_TIME = 2 * 60 * 1000;

const getCacheKey = (username) =>
  `todayBetCache_${username}`;


/* =========================================================
   HELPERS
========================================================= */

const getDateValue = (value) => {

  if (!value) {
    return null;
  }

  try {

    if (
      value?.toDate &&
      typeof value.toDate === "function"
    ) {
      return value.toDate();
    }

    if (value?.seconds) {
      return new Date(
        value.seconds * 1000
      );
    }

    if (value instanceof Date) {
      return value;
    }

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return null;
    }

    return date;

  } catch {
    return null;
  }
};


/* =========================================================
   TODAY
========================================================= */

const isToday = (value) => {

  const date =
    getDateValue(value);

  if (!date) {
    return false;
  }

  const today =
    new Date();

  return (
    date.getFullYear() ===
      today.getFullYear() &&

    date.getMonth() ===
      today.getMonth() &&

    date.getDate() ===
      today.getDate()
  );
};


/* =========================================================
   FORMAT TIME
========================================================= */

const formatTime = (value) => {

  const date =
    getDateValue(value);

  if (!date) {
    return "--:--";
  }

  return date.toLocaleTimeString(
    [],
    {
      hour: "2-digit",
      minute: "2-digit",
    }
  );
};


/* =========================================================
   FORMAT DATE
========================================================= */

const formatDate = (value) => {

  const date =
    getDateValue(value);

  if (!date) {
    return "N/A";
  }

  return date.toLocaleDateString(
    [],
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  );
};


/* =========================================================
   FORMAT NUMBER
========================================================= */

const formatNumber = (value) => {

  const number =
    Number(value || 0);

  if (
    Number.isNaN(number)
  ) {
    return "0";
  }

  return number.toLocaleString();
};


/* =========================================================
   STATUS
========================================================= */

const normalizeStatus = (
  status
) => {

  if (!status) {
    return "pending";
  }

  const value =
    String(status)
      .trim()
      .toLowerCase();

  if (
    value === "success" ||
    value === "successful" ||
    value === "completed" ||
    value === "complete" ||
    value === "paid"
  ) {
    return "success";
  }

  if (
    value === "failed" ||
    value === "failure" ||
    value === "cancelled" ||
    value === "canceled" ||
    value === "rejected"
  ) {
    return "failed";
  }

  return "pending";
};


/* =========================================================
   STATUS ICON
========================================================= */

const StatusIcon = ({
  status,
}) => {

  if (
    status === "success"
  ) {
    return <FaCheckCircle />;
  }

  if (
    status === "failed"
  ) {
    return <FaTimesCircle />;
  }

  return <FaHourglassHalf />;
};


/* =========================================================
   TODAY BET
========================================================= */

const TodayBet = () => {

  const navigate =
    useNavigate();


  /* =======================================================
     STATE
  ======================================================= */

  const [levels, setLevels] =
    useState({
      1: [],
      2: [],
      3: [],
    });


  const [activeLevel, setActiveLevel] =
    useState(1);


  const [expandedBetId, setExpandedBetId] =
    useState(null);


  const [loading, setLoading] =
    useState(true);


  const [refreshing, setRefreshing] =
    useState(false);


  const [error, setError] =
    useState("");


  const loggedInUsername =
    localStorage.getItem(
      "username"
    );


  /* =======================================================
     CACHE
  ======================================================= */

  const saveCache = (
    data
  ) => {

    if (
      !loggedInUsername
    ) {
      return;
    }

    try {

      localStorage.setItem(
        getCacheKey(
          loggedInUsername
        ),
        JSON.stringify({
          data,
          updatedAt:
            Date.now(),
        })
      );

    } catch (err) {

      console.error(
        "Failed saving bet cache:",
        err
      );

    }

  };


  const loadCache = () => {

    if (
      !loggedInUsername
    ) {
      return null;
    }

    try {

      const raw =
        localStorage.getItem(
          getCacheKey(
            loggedInUsername
          )
        );

      if (!raw) {
        return null;
      }

      const cached =
        JSON.parse(raw);

      if (
        !cached?.data
      ) {
        return null;
      }

      return cached;

    } catch (err) {

      console.error(
        "Failed loading bet cache:",
        err
      );

      return null;

    }

  };


  /* =======================================================
     CURRENT USER
  ======================================================= */

  const fetchCurrentUser =
    async () => {

      if (
        !loggedInUsername
      ) {
        return null;
      }

      const q =
        query(
          collection(
            db,
            "users"
          ),
          where(
            "username",
            "==",
            loggedInUsername
          ),
          limit(1)
        );

      const snap =
        await getDocs(q);

      if (
        snap.empty
      ) {
        return null;
      }

      return {
        id:
          snap.docs[0].id,

        ...snap.docs[0].data(),
      };

    };


  /* =======================================================
     FETCH USER BETS
  ======================================================= */

  const fetchUserBets =
    async (
      username
    ) => {

      if (!username) {
        return [];
      }

      try {

        const q =
          query(
            collection(
              db,
              "antscore"
            ),
            where(
              "username",
              "==",
              username
            )
          );

        const snap =
          await getDocs(q);

        const bets = [];


        snap.forEach(
          (doc) => {

            const data =
              doc.data();


            /*
             * IMPORTANT:
             * Today means the day the
             * bet was CREATED.
             */

            if (
              !isToday(
                data.createdAt
              )
            ) {
              return;
            }


            bets.push({

              id:
                doc.id,

              betId:
                data.betId ||
                doc.id,

              username:
                data.username ||
                username,

              amount:
                Number(
                  data.amount ??
                  data.stake ??
                  data.quantity ??
                  0
                ),

              stake:
                Number(
                  data.stake ??
                  data.amount ??
                  0
                ),

              currency:
                data.currency ||
                "TZS",

              createdAt:
                data.createdAt,

              league:
                data.league ||
                "N/A",

              teamA:
                data.teamA ||
                "N/A",

              teamB:
                data.teamB ||
                "N/A",

              odds:
                Number(
                  data.odds || 0
                ),

              result:
                data.result ||
                "N/A",

              status:
                normalizeStatus(
                  data.status
                ),

              score:
                data.score ||
                "N/A",

              totalWin:
                Number(
                  data.totalWin ||
                  0
                ),

              profit:
                Number(
                  data.profit ||
                  0
                ),

              matchDate:
                data.matchDate ||
                "",

              matchDateText:
                data.matchDateText ||
                "",

              matchTime:
                data.matchTime ||
                "",

              matchTimestamp:
                data.matchTimestamp ||
                null,

              paidAt:
                data.paidAt ||
                null,

              quantity:
                Number(
                  data.quantity ||
                  0
                ),

              mode:
                data.mode ||
                "ant",

              type:
                data.type ||
                "antscore",

              raw:
                data,

            });

          }
        );


        /*
         * Latest bet first
         */

        bets.sort(
          (a, b) => {

            const dateA =
              getDateValue(
                a.createdAt
              );

            const dateB =
              getDateValue(
                b.createdAt
              );

            return (
              (dateB?.getTime() || 0) -
              (dateA?.getTime() || 0)
            );

          }
        );


        return bets;

      } catch (err) {

        console.error(
          `Failed fetching bets for ${username}:`,
          err
        );

        return [];

      }

    };


  /* =======================================================
     FETCH LEVEL USERS
  ======================================================= */

  const fetchLevelUsers =
    async (
      referralCodes,
      maxLevel = 3
    ) => {

      const result = {
        1: [],
        2: [],
        3: [],
      };


      let currentCodes =
        referralCodes;

      let level = 1;


      while (
        level <= maxLevel &&
        currentCodes.length > 0
      ) {

        const chunks = [];


        /*
         * Firestore IN max = 10
         */

        for (
          let i = 0;
          i < currentCodes.length;
          i += 10
        ) {

          chunks.push(
            currentCodes.slice(
              i,
              i + 10
            )
          );

        }


        let users = [];


        const snapshots =
          await Promise.all(
            chunks.map(
              async (chunk) => {

                try {

                  const q =
                    query(
                      collection(
                        db,
                        "users"
                      ),
                      where(
                        "whoReferredWho",
                        "in",
                        chunk
                      )
                    );

                  return await getDocs(
                    q
                  );

                } catch (err) {

                  console.error(
                    "Level fetch error:",
                    err
                  );

                  return null;

                }

              }
            )
          );


        snapshots.forEach(
          (snap) => {

            if (!snap) {
              return;
            }

            snap.forEach(
              (doc) => {

                users.push({
                  id:
                    doc.id,

                  ...doc.data(),
                });

              }
            );

          }
        );


        /*
         * Remove duplicates
         */

        users =
          Array.from(
            new Map(
              users.map(
                (user) => [
                  user.id,
                  user,
                ]
              )
            ).values()
          );


        /*
         * Fetch today's bets
         */

        const usersWithBets =
          await Promise.all(
            users.map(
              async (user) => {

                const bets =
                  await fetchUserBets(
                    user.username
                  );


                const totalBet =
                  bets.reduce(
                    (
                      total,
                      bet
                    ) =>
                      total +
                      Number(
                        bet.amount ||
                        0
                      ),
                    0
                  );


                return {

                  ...user,

                  bets,

                  totalBet,

                };

              }
            )
          );


        /*
         * Only users who
         * bet today.
         */

        result[level] =
          usersWithBets.filter(
            (user) =>
              user.bets.length >
              0
          );


        /*
         * Continue network
         */

        currentCodes =
          users
            .map(
              (user) =>
                user.referralCode
            )
            .filter(Boolean);


        level++;

      }


      return result;

    };


  /* =======================================================
     LOAD
  ======================================================= */

  const loadTodayBets =
    async (
      showRefresh = false
    ) => {

      if (
        !loggedInUsername
      ) {

        setLoading(false);

        return;

      }


      if (showRefresh) {
        setRefreshing(true);
      }


      setError("");


      try {

        const user =
          await fetchCurrentUser();


        if (
          !user?.referralCode
        ) {

          const empty = {
            1: [],
            2: [],
            3: [],
          };

          setLevels(empty);

          saveCache(empty);

          return;

        }


        const data =
          await fetchLevelUsers(
            [
              user.referralCode,
            ],
            3
          );


        setLevels(data);

        saveCache(data);

      } catch (err) {

        console.error(
          "Failed loading today's bets:",
          err
        );

        setError(
          "Failed to load today's bets."
        );

      } finally {

        setLoading(false);

        setRefreshing(false);

      }

    };


  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {

    if (
      !loggedInUsername
    ) {

      setLoading(false);

      return;

    }


    const cached =
      loadCache();


    if (
      cached?.data
    ) {

      setLevels({
        1:
          cached.data[1] ||
          [],

        2:
          cached.data[2] ||
          [],

        3:
          cached.data[3] ||
          [],
      });

      setLoading(false);

    }


    loadTodayBets(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUsername]);


  /* =======================================================
     AUTO REFRESH
  ======================================================= */

  useEffect(() => {

    if (
      !loggedInUsername
    ) {
      return;
    }


    const interval =
      setInterval(
        () => {

          loadTodayBets(
            false
          );

        },
        CACHE_TIME
      );


    return () => {

      clearInterval(
        interval
      );

    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUsername]);


  /* =======================================================
     ACTIVE DATA
  ======================================================= */

  const currentUsers =
    levels[activeLevel] ||
    [];


  const allBets =
    currentUsers.flatMap(
      (user) =>
        user.bets || []
    );


  const totalBet =
    allBets.reduce(
      (total, bet) =>
        total +
        Number(
          bet.amount || 0
        ),
      0
    );


  const totalUsers =
    currentUsers.length;


  const totalBets =
    allBets.length;


  /* =======================================================
     TOGGLE
  ======================================================= */

  const toggleBet = (
    id
  ) => {

    setExpandedBetId(
      (previous) =>
        previous === id
          ? null
          : id
    );

  };


  /* =======================================================
     LOGIN
  ======================================================= */

  if (
    !loggedInUsername
  ) {

    return (

      <div className="today-bet-wrapper">

        <div className="today-bet-empty">

          <div className="today-bet-empty-icon">
            <FaUser />
          </div>

          <h3>
            Login Required
          </h3>

          <p>
            Please login first to
            view your team's bets.
          </p>

          <button
            type="button"
            className="today-bet-back-empty"
            onClick={() =>
              navigate(-1)
            }
          >
            <FaArrowLeft />
            Back
          </button>

        </div>

      </div>

    );

  }


  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {

    return (

      <div className="today-bet-wrapper">

        <div className="today-bet-loading">

          <div className="today-bet-spinner" />

          <strong>
            Loading today's bets
          </strong>

          <span>
            Checking team betting activity...
          </span>

        </div>

      </div>

    );

  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (

    <div className="today-bet-wrapper">


      {/* =================================================
          HEADER
      ================================================= */}

      <div className="today-bet-header">

        <button
          type="button"
          className="today-bet-back-btn"
          onClick={() =>
            navigate(-1)
          }
        >
          <FaArrowLeft />

          <span>
            Back
          </span>
        </button>


        <div className="today-bet-title">

          <div className="today-bet-title-icon">

            <FaFutbol />

          </div>

          <div>

            <span>
              TEAM ACTIVITY
            </span>

            <h1>
              Today Bet
            </h1>

          </div>

        </div>


        <button
          type="button"
          className={
            refreshing
              ? "today-bet-refresh spinning"
              : "today-bet-refresh"
          }
          onClick={() =>
            loadTodayBets(true)
          }
          title="Refresh"
        >

          <FaSyncAlt />

        </button>

      </div>


      {/* =================================================
          ERROR
      ================================================= */}

      {error && (

        <div className="today-bet-error">

          <span>
            {error}
          </span>

          <button
            type="button"
            onClick={() =>
              loadTodayBets(true)
            }
          >
            Retry
          </button>

        </div>

      )}


      {/* =================================================
          SUMMARY
      ================================================= */}

      <div className="today-bet-summary">

        <div className="today-bet-summary-icon">

          <FaMoneyBillWave />

        </div>


        <div className="today-bet-summary-main">

          <span>
            TOTAL BET — LEVEL {activeLevel}
          </span>

          <strong>
            {formatNumber(
              totalBet
            )}
          </strong>

          <small>
            TZS
          </small>

        </div>


        <div className="today-bet-summary-side">

          <strong>
            {totalBets}
          </strong>

          <span>
            Bets
          </span>

        </div>

      </div>


      {/* =================================================
          STATS
      ================================================= */}

      <div className="today-bet-stats">

        <div className="today-bet-stat">

          <div className="today-bet-stat-icon">

            <FaUsers />

          </div>

          <div>

            <span>
              USERS
            </span>

            <strong>
              {totalUsers}
            </strong>

          </div>

        </div>


        <div className="today-bet-stat">

          <div className="today-bet-stat-icon">

            <FaFutbol />

          </div>

          <div>

            <span>
              BETS
            </span>

            <strong>
              {totalBets}
            </strong>

          </div>

        </div>


        <div className="today-bet-stat">

          <div className="today-bet-stat-icon">

            <FaTrophy />

          </div>

          <div>

            <span>
              LEVEL
            </span>

            <strong>
              {activeLevel}
            </strong>

          </div>

        </div>

      </div>


      {/* =================================================
          LEVEL TABS
      ================================================= */}

      <div className="today-bet-level-tabs">

        {[1, 2, 3].map(
          (level) => {

            const count =
              levels[level]?.length ||
              0;


            return (

              <button
                key={level}
                type="button"
                className={
                  activeLevel === level
                    ? "today-bet-level-tab active"
                    : "today-bet-level-tab"
                }
                onClick={() => {

                  setActiveLevel(
                    level
                  );

                  setExpandedBetId(
                    null
                  );

                }}
              >

                <span>
                  LEVEL {level}
                </span>

                <strong>
                  {count}
                </strong>

              </button>

            );

          }
        )}

      </div>


      {/* =================================================
          SECTION HEAD
      ================================================= */}

      <div className="today-bet-section-head">

        <div>

          <span>
            TODAY'S BETTING ACTIVITY
          </span>

          <strong>
            Level {activeLevel}
          </strong>

        </div>


        <div className="today-bet-section-count">

          {totalUsers}

          <span>
            users
          </span>

        </div>

      </div>


      {/* =================================================
          EMPTY
      ================================================= */}

      {currentUsers.length === 0 ? (

        <div className="today-bet-empty">

          <div className="today-bet-empty-icon">

            <FaFutbol />

          </div>

          <h3>
            No Bets Today
          </h3>

          <p>
            Hakuna member wa Level{" "}
            {activeLevel} aliyeweka
            bet leo.
          </p>

          <div className="today-bet-empty-date">

            <FaClock />

            <span>
              Checked today
            </span>

          </div>

        </div>

      ) : (

        /* =================================================
           USER LIST
        ================================================= */

        <div className="today-bet-list">

          {currentUsers.map(
            (user, index) => {

              const bets =
                [...(
                  user.bets || []
                )].sort(
                  (a, b) => {

                    const dateA =
                      getDateValue(
                        a.createdAt
                      );

                    const dateB =
                      getDateValue(
                        b.createdAt
                      );

                    return (
                      (dateB?.getTime() || 0) -
                      (dateA?.getTime() || 0)
                    );

                  }
                );


              return (

                <div
                  key={user.id}
                  className="today-bet-user-card"
                >


                  {/* USER HEADER */}

                  <div className="today-bet-user-header">

                    <div className="today-bet-number">

                      {String(
                        index + 1
                      ).padStart(
                        2,
                        "0"
                      )}

                    </div>


                    <div className="today-bet-avatar">

                      <FaUser />

                    </div>


                    <div className="today-bet-user-info">

                      <strong>
                        {user.username ||
                          "Unknown"}
                      </strong>

                      <span>
                        Level {activeLevel}
                      </span>

                    </div>


                    <div className="today-bet-user-total">

                      <span>
                        TOTAL BET
                      </span>

                      <strong>
                        {formatNumber(
                          user.totalBet
                        )}
                      </strong>

                      <small>
                        TZS
                      </small>

                    </div>

                  </div>


                  {/* BETS */}

                  <div className="today-bet-transactions">

                    {bets.map(
                      (bet) => {

                        const expanded =
                          expandedBetId ===
                          bet.id;


                        return (

                          <div
                            key={
                              bet.id
                            }
                            className={
                              expanded
                                ? "today-bet-card expanded"
                                : "today-bet-card"
                            }
                          >


                            {/* =================================================
                                COMPACT ROW
                            ================================================= */}

                            <button
                              type="button"
                              className="today-bet-row"
                              onClick={() =>
                                toggleBet(
                                  bet.id
                                )
                              }
                            >

                              <div className="today-bet-row-icon">

                                <FaFutbol />

                              </div>


                              <div className="today-bet-row-user">

                                <strong>
                                  {bet.username}
                                </strong>

                                <span>
                                  {formatNumber(
                                    bet.amount
                                  )}
                                </span>

                              </div>


                              <div className="today-bet-row-currency">

                                {bet.currency}

                              </div>


                              <div className="today-bet-row-time">

                                <FaClock />

                                {formatTime(
                                  bet.createdAt
                                )}

                              </div>


                              <div className="today-bet-row-arrow">

                                {expanded ? (
                                  <FaChevronUp />
                                ) : (
                                  <FaChevronRight />
                                )}

                              </div>

                            </button>


                            {/* =================================================
                                DETAILS
                            ================================================= */}

                            {expanded && (

                              <div className="today-bet-details">


                                {/* BET ID */}

                                <div className="today-bet-detail">

                                  <span>
                                    BET ID
                                  </span>

                                  <strong>
                                    {bet.betId}
                                  </strong>

                                </div>


                                {/* AMOUNT */}

                                <div className="today-bet-detail">

                                  <span>
                                    AMOUNT
                                  </span>

                                  <strong>
                                    {formatNumber(
                                      bet.amount
                                    )}{" "}
                                    {bet.currency}
                                  </strong>

                                </div>


                                {/* STAKE */}

                                <div className="today-bet-detail">

                                  <span>
                                    STAKE
                                  </span>

                                  <strong>
                                    {formatNumber(
                                      bet.stake
                                    )}{" "}
                                    {bet.currency}
                                  </strong>

                                </div>


                                {/* LEAGUE */}

                                <div className="today-bet-detail full">

                                  <span>
                                    LEAGUE
                                  </span>

                                  <strong>
                                    {bet.league}
                                  </strong>

                                </div>


                                {/* MATCH */}

                                <div className="today-bet-detail full">

                                  <span>
                                    MATCH
                                  </span>

                                  <strong>
                                    {bet.teamA}
                                    {" "}
                                    vs
                                    {" "}
                                    {bet.teamB}
                                  </strong>

                                </div>


                                {/* ODDS */}

                                <div className="today-bet-detail">

                                  <span>
                                    ODDS
                                  </span>

                                  <strong>
                                    {bet.odds}
                                  </strong>

                                </div>


                                {/* RESULT */}

                                <div className="today-bet-detail">

                                  <span>
                                    RESULT
                                  </span>

                                  <strong className="result-value">

                                    {bet.result}

                                  </strong>

                                </div>


                                 
                               

                                {/* SCORE */}

                                <div className="today-bet-detail">

                                  <span>
                                    SCORE
                                  </span>

                                  <strong>
                                    {bet.score}
                                  </strong>

                                </div>


                                {/* TOTAL WIN */}

                                <div className="today-bet-detail">

                                  <span>
                                    TOTAL WIN
                                  </span>

                                  <strong>
                                    {formatNumber(
                                      bet.totalWin
                                    )}{" "}
                                    {bet.currency}
                                  </strong>

                                </div>


                                {/* PROFIT */}

                                <div className="today-bet-detail">

                                  <span>
                                    PROFIT
                                  </span>

                                  <strong>
                                    {formatNumber(
                                      bet.profit
                                    )}{" "}
                                    {bet.currency}
                                  </strong>

                                </div>


                                {/* MATCH DATE */}

                                <div className="today-bet-detail">

                                  <span>
                                    MATCH DATE
                                  </span>

                                  <strong>
                                    {bet.matchDate ||
                                      formatDate(
                                        bet.matchTimestamp
                                      )}
                                  </strong>

                                </div>


                                {/* MATCH TIME */}

                                <div className="today-bet-detail">

                                  <span>
                                    MATCH TIME
                                  </span>

                                  <strong>
                                    {bet.matchTime ||
                                      "--:--"}
                                  </strong>

                                </div>


                                {/* CREATED */}

                                <div className="today-bet-detail">

                                  <span>
                                    BET CREATED
                                  </span>

                                  <strong>
                                    {formatDate(
                                      bet.createdAt
                                    )}{" "}
                                    {formatTime(
                                      bet.createdAt
                                    )}
                                  </strong>

                                </div>


                                {/* PAID AT */}

                                <div className="today-bet-detail">

                                  <span>
                                    PAID AT
                                  </span>

                                  <strong>
                                    {bet.paidAt
                                      ? `${formatDate(
                                          bet.paidAt
                                        )} ${formatTime(
                                          bet.paidAt
                                        )}`
                                      : "N/A"}
                                  </strong>

                                </div>


                              </div>

                            )}

                          </div>

                        );

                      }
                    )}

                  </div>

                </div>

              );

            }
          )}

        </div>

      )}


      {/* =================================================
          FOOTER
      ================================================= */}

      <div className="today-bet-footer">

        <span>
          TODAY BET ACTIVITY
        </span>

        <small>
          Showing bets created today
          from your team.
        </small>

      </div>

    </div>

  );

};


export default TodayBet;
